import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RoadmapItem } from "../api";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

// Which roadmap items this browser has already upvoted (per-viewer, best-effort).
function readVoted(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem("roadmapVoted") ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

function VoteButton({ item }: { item: RoadmapItem }) {
  const queryClient = useQueryClient();
  const [voted, setVoted] = useState(() => readVoted().has(item.id));
  const [optimistic, setOptimistic] = useState(0);
  const vote = useMutation({
    mutationFn: () => api.voteRoadmap(item.id),
    onSuccess: () => {
      const next = readVoted();
      next.add(item.id);
      try { localStorage.setItem("roadmapVoted", JSON.stringify([...next])); } catch { /* ignore */ }
      queryClient.invalidateQueries({ queryKey: ["updates"] });
    },
    onError: () => {
      // Roll back the optimistic vote so the button isn't left disabled with a
      // count that was never persisted.
      setVoted(false);
      setOptimistic(0);
    },
  });
  function cast() {
    if (voted || vote.isPending) return;
    setVoted(true);
    setOptimistic(1);
    vote.mutate();
  }
  return (
    <button
      onClick={cast}
      disabled={voted || vote.isPending}
      title={voted ? "You upvoted this" : "Upvote"}
      style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, borderRadius: 6, padding: "2px 6px", flex: "none", cursor: voted ? "default" : "pointer", border: `1px solid ${voted ? "#c8e0d3" : "#e7e5df"}`, background: voted ? "#e8f4ee" : "#f6f5f1", color: voted ? "#1f7a4d" : "#4a504a" }}
    >
      <span style={{ color: voted ? "#1f7a4d" : "#006747", fontSize: 9 }}>▲</span>{item.votes + optimistic}
    </button>
  );
}

const KIND_TONE: Record<string, { bg: string; fg: string }> = {
  Fixed: { bg: "#f7e4e0", fg: "#b3261e" },
  Improved: { bg: "#e8eef7", fg: "#2a5f9e" },
  New: { bg: "#e8f4ee", fg: "#1f7a4d" },
};
const BUCKETS: { key: "now" | "next" | "later"; label: string; color: string }[] = [
  { key: "now", label: "Now", color: "#006747" },
  { key: "next", label: "Next", color: "#2a6f8a" },
  { key: "later", label: "Later", color: "#8a8e88" },
];

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function Updates() {
  const { data, isLoading, error } = useQuery({ queryKey: ["updates"], queryFn: api.updates });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const card = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" } as const;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Changelog & roadmap</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          What shipped, what's coming (member-voted), and announcements with per-client read receipts.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1fr)", gap: 16, alignItems: "start" }}>
        {/* Changelog feed */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {data.releases.map((rel) => (
            <div key={rel.id} style={card}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Space Mono',monospace" }}>{rel.version}</span>
                <span style={{ fontSize: 11.5, color: "#8a8e88" }}>{fmtDate(rel.publishedAt)}</span>
              </div>
              {rel.note && <div style={{ fontSize: 12.5, color: "#7a7f78", fontStyle: "italic", marginBottom: 8 }}>{rel.note}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {rel.items.map((it, i) => {
                  const tone = KIND_TONE[it.kind] ?? { bg: "#f0efe9", fg: "#6b706a" };
                  return (
                    <div key={i} style={{ display: "flex", gap: 9 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: tone.bg, color: tone.fg, height: "fit-content", whiteSpace: "nowrap" }}>{it.kind}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{it.title}</div>
                        {it.body && <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 1 }}>{it.body}</div>}
                        {it.reportedBy && <div style={{ fontSize: 11, color: "#a8aca6", marginTop: 2 }}>reported by {it.reportedBy}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Roadmap + announcements */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Icon name="map" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Roadmap</h2>
            </div>
            {BUCKETS.map((b) => (
              <div key={b.key} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: b.color, marginBottom: 5 }}>{b.label}</div>
                {(data.roadmap[b.key] as RoadmapItem[]).map((r) => (
                  <div key={r.id} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "6px 0", borderTop: "1px solid #f4f3ee" }}>
                    <VoteButton item={r} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600 }}>{r.title}</div>
                      {r.body && <div style={{ fontSize: 11.5, color: "#8a8e88" }}>{r.body}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Icon name="megaphone" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Announcements</h2>
            </div>
            {data.announcements.map((a, i) => {
              const pct = a.totalClients ? Math.round((a.readCount / a.totalClients) * 100) : 0;
              return (
                <div key={a.id} style={{ padding: "9px 0", borderTop: i ? "1px solid #f4f3ee" : "none" }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</div>
                  {a.body && <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 1 }}>{a.body}</div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
                    {a.channels.map((ch) => (
                      <span key={ch} style={{ fontSize: 10, color: "#4a504a", background: "#f0efe9", borderRadius: 4, padding: "1px 6px" }}>{ch}</span>
                    ))}
                    <span style={{ fontSize: 11, color: "#8a8e88" }}>{a.scheduled ? `Scheduled · ${fmtDate(a.scheduledAt)}` : fmtDate(a.scheduledAt)}</span>
                    <div style={{ flex: 1 }} />
                    {a.scheduled ? (
                      <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#fbf1dd", color: "#9a7b1f" }}>upcoming</span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 600, color: pct >= 60 ? "#1f7a4d" : "#9a7b1f", fontFamily: "'Space Mono',monospace" }}>{a.readCount}/{a.totalClients} read</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Page>
  );
}
